/**
 * Reading and writing the merged document's `project-redaction-data` asset
 * (VE-30104).
 *
 * Reads are `takeLatest`; only the newest answer is wanted. WRITES ARE
 * SERIALISED through an action channel rather than dropped or cancelled —
 * the reducer applies each one optimistically before the saga sees it, so a
 * dropped write leaves the client showing a change that was never sent, and
 * cancelling cannot recall a request in flight.
 *
 * WHAT A TURN WRITES COMES FROM ITS OWN ACTION, not from the record, which
 * is only what it is composed onto: a SUCCESS lands between turns and
 * replaces the record, so a queued turn re-reading it would resend the
 * PREVIOUS write's marks under a fresh asset id and amber a whole release
 * for nothing. Results name the write they belong to (`writeId`).
 *
 * Two errands hang off a write and neither is on the reviewer's path:
 * artwork is RE-HOMED to the project first (`rehomeSetWideArtwork`), and
 * superseded bodies are trimmed in a FORK (`retireSupersededBodies`).
 *
 * The queue is also where a write is REFUSED when another user holds the
 * merged document (VE-31538, `ensureSetUnlocked`). These settings apply to
 * the whole set, and the queue is the one point all three writes pass
 * through — and the right place for a check that costs a round trip.
 */
import {
  actionChannel,
  call,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'typed-redux-saga/macro';

import { enqueueSnackbar } from '@common-modules/snackbar';
import { ensureSetUnlocked } from './setLockGate';
import { sagaIntl } from '@i18n';
import { selectToken, selectGraphQLConfig } from '../documents/sagas/auth';
import {
  serviceLoadWatermarkImageAsset,
  serviceFindWatermarkImageAsset,
  serviceSaveWatermarkImageAsset,
} from '@redact-modules/documents/services';
import { deriveWatermarkImage } from '@redact-modules/documents/watermarkImage';
import { allowedRasterContainer } from './projectRedactionAsset';
import type { AppliedWatermark } from '@redact-modules/documents/models';
import type { TDOId } from '@common-modules/universal/models/Brands';
import type { ProjectRedactionData } from './projectRedactionAsset';
import {
  LOAD_PROJECT_REDACTION_FAILURE,
  LOAD_PROJECT_REDACTION_REQUEST,
  LOAD_PROJECT_REDACTION_SUCCESS,
  LOAD_PROJECT_SUCCESS,
  LOAD_PROJECT_FAILURE,
  SET_PROJECT_WATERMARKS_FAILURE,
  SET_PROJECT_BATES_FAILURE,
  SET_PROJECT_WATERMARKS_REQUEST,
  SET_PROJECT_BATES_REQUEST,
  CLEAR_PROJECT_BATES_REQUEST,
  SET_PROJECT_WATERMARKS_SUCCESS,
} from './actions';
import { selectProjectMembers, selectProjectRedaction } from './selectors';
import {
  serviceLoadProjectRedactionData,
  serviceWriteProjectRedactionData,
  retireSupersededBodies,
} from './projectRedactionServices';

/**
 * Read a project's record.
 *
 * `takeLatest`: several surfaces ask for the same project — the editor on
 * open, the documents panel, a retry after a failure — and only the newest
 * answer is wanted. An older reply landing after a newer one would move the
 * record backwards.
 */
export function* handleLoadProjectRedaction(
  action: ReturnType<typeof LOAD_PROJECT_REDACTION_REQUEST>
) {
  const { projectTdoId } = action.payload;
  try {
    const token: string = yield* select(selectToken);
    const cfg = yield* select(selectGraphQLConfig);
    const loaded = yield* call(() =>
      serviceLoadProjectRedactionData(
        projectTdoId,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      )
    );
    // HYDRATED BEFORE SUCCESS, so no reader ever sees a half-built record.
    // The marks arrive carrying an `imageAssetId` and no raster, and an image
    // mark with nothing to draw renders nothing, exports nothing and reports
    // itself applied — which is the worst of the three.
    const hydrated = loaded
      ? yield* call(
          hydrateProjectWatermarkImages,
          projectTdoId,
          yield* call(membersForHydration, projectTdoId, loaded.data),
          loaded.data,
          token,
          cfg
        )
      : null;
    yield* put(
      LOAD_PROJECT_REDACTION_SUCCESS({
        projectTdoId,
        data: hydrated?.data ?? null,
        artworkFailures: hydrated?.failedInstanceIds,
      })
    );
  } catch (err) {
    // The name, not the message: it interpolates the server's GraphQL text,
    // which can echo operator-authored watermark text.
    console.warn(
      '[redact] merged document settings read failed',
      err instanceof Error ? err.name : 'unknown error'
    );
    // NO SNACKBAR. This runs on open, for every member of every merged
    // document, and a failure degrades to "the set's marks are not drawn"
    // which the banner reports in context. An error toast on page load for a
    // read nobody asked for is noise.
    yield* put(LOAD_PROJECT_REDACTION_FAILURE({ projectTdoId }));
  }
}

/**
 * Make sure every set-wide image mark's raster lives on the PROJECT.
 *
 * The set's record outlives any one member, so what it points at must too —
 * the lifecycle rule `watermarkImageAsset` states for the other direction.
 *
 * COPIES RATHER THAN MOVES, and deletes nothing: the member may still
 * reference the same asset, and an unreferenced one is just the copy the
 * next apply reuses. Best-effort per mark — one that cannot be re-homed
 * keeps the pointer it already had.
 */
function* rehomeSetWideArtwork(
  projectTdoId: TDOId,
  marks: readonly AppliedWatermark[],
  token: string,
  cfg: {
    readonly apiRoot: string;
    readonly graphQLEndpoint: string;
    readonly veritoneAppId: string;
  }
) {
  const strays = marks.some(
    (w) =>
      w.type === 'image' &&
      !!w.imageAssetContainerId &&
      String(w.imageAssetContainerId) !== String(projectTdoId)
  );
  if (!strays) {
    return marks;
  }
  const moved: AppliedWatermark[] = [];
  // Keyed by the asset being replaced, because two instances of one library
  // entry share a raster and must not upload it twice.
  const relocated = new Map<string, string>();
  for (const mark of marks) {
    if (
      mark.type !== 'image' ||
      !mark.imageAssetContainerId ||
      String(mark.imageAssetContainerId) === String(projectTdoId)
    ) {
      moved.push(mark);
      continue;
    }
    const from = mark.imageAssetId;
    const base = mark.imageBaseDataUrl;
    if (!from || !base) {
      // Nothing to copy from — rewriting the pointer would claim a copy that
      // was never made.
      moved.push(mark);
      continue;
    }
    const already = relocated.get(from);
    if (already) {
      moved.push({
        ...mark,
        imageAssetId: already,
        imageAssetContainerId: String(projectTdoId),
      });
      continue;
    }
    try {
      const existing = yield* call(
        serviceFindWatermarkImageAsset,
        projectTdoId,
        mark.watermarkId ?? null,
        base,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
      const id =
        existing ??
        (yield* call(
          serviceSaveWatermarkImageAsset,
          projectTdoId,
          base,
          mark.imageName ?? null,
          mark.watermarkId ?? null,
          token,
          cfg.apiRoot,
          cfg.graphQLEndpoint
        )).id;
      relocated.set(from, id);
      moved.push({
        ...mark,
        imageAssetId: id,
        imageAssetContainerId: String(projectTdoId),
      });
    } catch (err) {
      console.warn(
        '[redact] merged document watermark artwork could not be re-homed',
        err instanceof Error ? err.name : 'unknown error'
      );
      moved.push(mark);
    }
  }
  return moved;
}

/**
 * Store the merged document's watermarks.
 *
 * SERIALISED, NOT DROPPED — see `watchProjectRedaction` for why dropping one
 * is not available here. A failure puts the previous record back.
 */
export function* handleSetProjectWatermarks(
  action: ReturnType<typeof SET_PROJECT_WATERMARKS_REQUEST>
) {
  const { projectTdoId, watermarks, watermarksOff, writeId } = action.payload;
  // The reducer ran first — redux-saga calls `next(action)` before putting the
  // action on its channel — so this carries the version it assigned, which
  // beats recomputing one here and getting a different answer from the one
  // already on screen.
  //
  // THE MARKS COME FROM THE ACTION, not from this slot. Writes serialise, so
  // a queued turn finds the slot already through someone else's result — and
  // a SUCCESS puts THAT write's marks in it. See the header.
  const applied = yield* select(selectProjectRedaction(projectTdoId));
  if (applied.status !== 'loaded') {
    // Unreachable through the panel, which does not open until the record has
    // been read. Asserted rather than assumed: writing a guessed version is
    // how an output's provenance stops meaning anything.
    yield* put(SET_PROJECT_WATERMARKS_FAILURE({ projectTdoId, writeId }));
    return;
  }
  try {
    const token: string = yield* select(selectToken);
    const cfg = yield* select(selectGraphQLConfig);
    // THE ARTWORK MOVES WITH THE MARK, before the record claims it.
    //
    // Scope is chosen per mark and can be changed afterwards, but the raster's
    // container was decided once, when the mark was added. So promoting a
    // file-only image mark to the whole set wrote a project record pointing at
    // a PNG on one member's TDO. That reads for as long as the member is in
    // the set — and the moment it leaves, or is deleted, the mark is
    // undrawable for EVERY member, with no asset id recorded, so the staleness
    // badge goes amber and re-burning declines for the same reason. A document
    // that left the set took the whole release's marking with it.
    //
    // Idempotent, so the common case costs one query: `find` returns the
    // existing copy whenever the project already holds this raster, which it
    // does for every mark added set-wide in the first place.
    const sending: ProjectRedactionData = {
      ...applied.data,
      watermarks: yield* call(
        rehomeSetWideArtwork,
        projectTdoId,
        watermarks,
        token,
        cfg
      ),
      watermarksOff,
    };
    const written = yield* call(() =>
      serviceWriteProjectRedactionData(
        projectTdoId,
        // Stamped with the project it belongs to, so a body copied or
        // restored onto another one is caught on read rather than quietly
        // marking the wrong release.
        {
          ...sending,
          projectTdoId: String(projectTdoId),
          // The watermarks changed HERE, so this write stamps them. Bates is
          // untouched and carries its own stamp and settings forward through
          // the spread above — which is the whole point of a stamp per
          // concern: a mark edit must not stale every member's numbering.
          watermarksModifiedDateTime: new Date().toISOString(),
        },
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint
      )
    );
    // The server's id for what was just stored. Provenance only now: outputs
    // record the CONCERN's stamp, not the body's asset, so this is what the
    // record was read from rather than what anything compares against.
    yield* put(
      SET_PROJECT_WATERMARKS_SUCCESS({
        projectTdoId,
        writeId,
        data: {
          ...sending,
          assetId: written.id,
        },
      })
    );
    // AFTER the success, and detached. Trimming the superseded bodies is
    // best-effort housekeeping — it swallows its own errors and nothing reads
    // its result — so it must not sit between the reviewer's Apply and the
    // panel saying Updated. Awaited, it is a history query plus up to 99
    // sequential deletes on that path.
    yield* fork(
      retireSupersededBodies,
      projectTdoId,
      written.id,
      written.createdDateTime,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
  } catch (err) {
    // The name, not the message: it interpolates the server's GraphQL text,
    // which can echo operator-authored watermark text.
    console.warn(
      '[redact] merged document watermark write failed',
      err instanceof Error ? err.name : 'unknown error'
    );
    // Rolled back from the slot the REDUCER stashed. Nothing is reconstructed
    // here: by the time this runs the previous record is gone from the slice.
    yield* put(SET_PROJECT_WATERMARKS_FAILURE({ projectTdoId, writeId }));
    yield* put(
      enqueueSnackbar({
        message: sagaIntl().formatMessage({
          id: 'mergedDocumentWatermarkFailed',
          defaultMessage:
            'Could not apply the watermark to the merged document.',
        }),
        variant: 'error',
      })
    );
  }
}

/**
 * Fetch the raster for every image mark in a set's record.
 *
 * FROM THE PROJECT TDO, which is where a set-wide mark's artwork is written —
 * `serviceLoadWatermarkImageAsset` checks the container and fails closed on a
 * mismatch, so passing the member's id here would reject every one of them.
 *
 * Sequential rather than parallel: a set carries a handful of marks at most,
 * they are usually the same library entry sharing one asset, and the loader
 * vets each raster by decoding it. Fanning those decodes out buys nothing and
 * costs a burst of memory on a page that is already loading a PDF.
 *
 * A mark whose raster will not load KEEPS its `imageAssetId` and arrives with
 * nothing to draw — clearing the id would turn one bad request into permanent
 * loss on the next write. Its instance id is RETURNED so the caller can report
 * it. An earlier version of this comment claimed the panel already reported
 * such a row; it did not. That badge needs either a report or a mark with no
 * pointer at all, and a failed hydration leaves the pointer in place — so the
 * row looked healthy while the page drew nothing and the burn dropped it.
 *
 * THE DRAWN FORM IS DERIVED HERE TOO, not just the base. `watermarksForStorage`
 * drops `imageDataUrl` on the way out because it is a pure function of the
 * base plus the grayscale and tint choices — and `toBurnInWatermark` returns
 * null for an image mark without it, while the overlay renders it and nothing
 * else. Restoring only the base therefore produced a mark that the panel still
 * listed, the page did not draw, and the burn silently dropped: applied,
 * invisible, and absent from the released file. `openDocument` does the same
 * derivation for a document's own marks, through the same function.
 */
/**
 * The set's members, WAITING for the read if a raster depends on the answer.
 *
 * `allowedRasterContainer` allowlists the project and its members, so an
 * unread membership REFUSES any raster living on a member. The editor
 * dispatches that read and this one in the same tick with nothing ordering
 * them, and a losing race is permanent: the record caches as `loaded`,
 * nothing re-hydrates it, and the `unresolved` retry cannot help because
 * governance is `governed`.
 *
 * Waits only when it would change the answer — a record whose rasters all
 * name the project (every record this app now writes) needs no membership,
 * so the common case stays one round trip. Settles on FAILURE too.
 */
function* membersForHydration(projectTdoId: TDOId, data: ProjectRedactionData) {
  const known = yield* select(selectProjectMembers(projectTdoId));
  if (known !== undefined) {
    return known;
  }
  const dependsOnMembers = data.watermarks.some(
    (w) =>
      w.type === 'image' &&
      !w.imageBaseDataUrl &&
      !!w.imageAssetId &&
      !!w.imageAssetContainerId &&
      String(w.imageAssetContainerId) !== String(projectTdoId)
  );
  if (!dependsOnMembers) {
    return known;
  }
  yield* take(
    (a: { type: string; payload?: { projectTdoId?: unknown } }) =>
      (a.type === LOAD_PROJECT_SUCCESS.type ||
        a.type === LOAD_PROJECT_FAILURE.type) &&
      String(a.payload?.projectTdoId) === String(projectTdoId)
  );
  return yield* select(selectProjectMembers(projectTdoId));
}

function* hydrateProjectWatermarkImages(
  projectTdoId: TDOId,
  /** The set's members — the only other containers a raster may name. */
  members: readonly TDOId[] | undefined,
  data: ProjectRedactionData,
  token: string,
  cfg: {
    readonly apiRoot: string;
    readonly graphQLEndpoint: string;
    readonly veritoneAppId: string;
  }
) {
  const needed = data.watermarks.some(
    (w) => w.type === 'image' && !w.imageBaseDataUrl && !!w.imageAssetId
  );
  if (!needed) {
    return { data, failedInstanceIds: [] as readonly string[] };
  }
  const failedInstanceIds: string[] = [];
  // One fetch per ASSET, not per mark: two instances of the same library
  // entry share an asset by design, and the panel applies the same watermark
  // twice often enough (two positions, two angles) for it to matter.
  const byAsset = new Map<string, string | null>();
  const hydrated: AppliedWatermark[] = [];
  for (const mark of data.watermarks) {
    if (mark.type !== 'image' || mark.imageBaseDataUrl || !mark.imageAssetId) {
      hydrated.push(mark);
      continue;
    }
    const assetId = mark.imageAssetId;
    // The container the mark RECORDS, not the project we are reading for. A
    // mark promoted to set-wide keeps a raster on the member it came from,
    // and the loader fails closed on a mismatch — inferring the project here
    // left every other member with an image mark it could list but not draw.
    // ALLOWLISTED, not taken on the body's word — see
    // `allowedRasterContainer`. A member's own TDO is legitimate here
    // because a mark promoted to the set keeps its raster where it was.
    const container = allowedRasterContainer({
      recorded: mark.imageAssetContainerId,
      fallback: projectTdoId,
      allowed: members ?? [],
    });
    if (!container) {
      console.warn(
        '[redact] merged document watermark names a container outside this project'
      );
      failedInstanceIds.push(mark.instanceId);
      hydrated.push(mark);
      continue;
    }
    if (!byAsset.has(assetId)) {
      let base: string | null = null;
      try {
        base = yield* call(
          serviceLoadWatermarkImageAsset,
          assetId,
          container,
          token,
          cfg.apiRoot,
          cfg.graphQLEndpoint,
          cfg.veritoneAppId
        );
      } catch (err) {
        // Logged, not thrown: one unreadable raster must not take the set's
        // other marks down with it.
        console.warn(
          '[redact] merged document watermark raster could not be loaded',
          err instanceof Error ? err.name : 'unknown error'
        );
      }
      byAsset.set(assetId, base);
    }
    const base = byAsset.get(assetId) ?? null;
    if (!base) {
      // REPORTED, not merely skipped. The mark keeps its pointer — clearing
      // it would turn one bad request into permanent loss on the next write —
      // and a mark with a pointer and no verdict reads as "still loading" to
      // the panel, so the id is what turns the row amber.
      failedInstanceIds.push(mark.instanceId);
      hydrated.push(mark);
      continue;
    }
    try {
      const drawn = yield* call(deriveWatermarkImage, base, {
        grayscale: mark.grayscale,
        color: mark.color,
      });
      hydrated.push({
        ...mark,
        imageBaseDataUrl: base,
        imageDataUrl: drawn,
      });
    } catch (err) {
      // The base survived but the derivation did not. Keep the base — the
      // panel can still say the mark is here — and leave the drawn form
      // absent, which is the same state a failed fetch produces.
      console.warn(
        '[redact] merged document watermark could not be derived',
        err instanceof Error ? err.name : 'unknown error'
      );
      // The base survived but the drawn form did not, which draws and burns
      // exactly like no artwork at all — so it is reported the same way.
      failedInstanceIds.push(mark.instanceId);
      hydrated.push({ ...mark, imageBaseDataUrl: base });
    }
  }
  return {
    data: { ...data, watermarks: hydrated },
    failedInstanceIds: failedInstanceIds as readonly string[],
  };
}

import { handleClearProjectBates, handleSetProjectBates } from './combineSaga';

/**
 * Writes run ONE AT A TIME, in the order they were made.
 *
 * An `actionChannel` buffers them instead of dropping them, and the worker
 * re-reads the record at the top of each turn (`handleSetProjectWatermarks`
 * already does), so a queued write composes onto the one before it rather
 * than racing it.
 *
 * `takeLatest` would be wrong here, not merely different: cancelling the saga
 * does not cancel the request already in flight, and the reader takes the
 * NEWEST asset by `createdDateTime` — so a cancelled write can still land
 * last and win.
 */
function* watchProjectRedactionWrites() {
  // ALL THREE ON ONE CHANNEL, because they all write the same body. Bates and
  // the watermarks became co-tenants of `project-redaction-data`, and the body
  // is written whole — so a Bates write read-modify-writes the marks and vice
  // versa. Two queues, or a queue beside a `takeLeading`, would let one
  // overwrite the other's turn with a body read before it landed.
  const channel = yield* actionChannel([
    SET_PROJECT_WATERMARKS_REQUEST,
    SET_PROJECT_BATES_REQUEST,
    CLEAR_PROJECT_BATES_REQUEST,
  ]);
  while (true) {
    const action = yield* take(channel);

    // VE-31538. Bates and watermark settings apply to the whole merged
    // document, so writing them under somebody else's session changes the
    // output they are working towards. Gated here rather than in the three
    // handlers because the queue is the one place all three pass through —
    // and because the gate does a round trip, which belongs in the
    // serialised section, not racing beside it.
    if (!(yield* ensureSetUnlocked(action.payload.projectTdoId))) {
      if (action.type === SET_PROJECT_WATERMARKS_REQUEST.type) {
        const watermarkAction = action as ReturnType<
          typeof SET_PROJECT_WATERMARKS_REQUEST
        >;
        yield* put(
          SET_PROJECT_WATERMARKS_FAILURE({
            projectTdoId: watermarkAction.payload.projectTdoId,
            writeId: watermarkAction.payload.writeId,
          })
        );
      } else {
        yield* put(
          SET_PROJECT_BATES_FAILURE({
            projectTdoId: action.payload.projectTdoId,
          })
        );
      }
      continue;
    }

    if (action.type === SET_PROJECT_BATES_REQUEST.type) {
      yield* call(
        handleSetProjectBates,
        action as ReturnType<typeof SET_PROJECT_BATES_REQUEST>
      );
    } else if (action.type === CLEAR_PROJECT_BATES_REQUEST.type) {
      yield* call(handleClearProjectBates, action);
    } else {
      yield* call(
        handleSetProjectWatermarks,
        action as ReturnType<typeof SET_PROJECT_WATERMARKS_REQUEST>
      );
    }
  }
}

export function* watchProjectRedaction() {
  yield* takeLatest(LOAD_PROJECT_REDACTION_REQUEST, handleLoadProjectRedaction);
  yield* fork(watchProjectRedactionWrites);
}
